import { DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormField, form, schema, submit, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { firstValueFrom } from 'rxjs';
import { MceApiService, UserCreditSummaryDto } from '../../core/mceapi.client.generated';
import { appOpt, getApiErrorMessage } from '../../core/mceapi.client.helpers';
import { isSome } from '../../shared/functions/functional';

interface UsagePeriodModel {
  startDate: Date;
  startTime: Date;
  endDate: Date;
  endTime: Date;
}

const isValidDate = (value: Date): boolean => value instanceof Date && !Number.isNaN(value.getTime());

const combineDateAndTime = (date: Date, time: Date): Date => {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
};

const usagePeriodSchema = schema<UsagePeriodModel>((path) => {
  validate(path.startDate, ({ value }) => isValidDate(value())
    ? undefined
    : { kind: 'invalidStartDate', message: 'Enter a valid start date' });
  validate(path.startTime, ({ value }) => isValidDate(value())
    ? undefined
    : { kind: 'invalidStartTime', message: 'Enter a valid start time' });
  validate(path.endDate, ({ value }) => isValidDate(value())
    ? undefined
    : { kind: 'invalidEndDate', message: 'Enter a valid end date' });
  validate(path.endTime, ({ value, valueOf }) => {
    const endTime = value();
    const startDate = valueOf(path.startDate);
    const startTime = valueOf(path.startTime);
    const endDate = valueOf(path.endDate);

    if (![startDate, startTime, endDate, endTime].every(isValidDate)) {
      return { kind: 'invalidEndTime', message: 'Enter a valid end time' };
    }

    return combineDateAndTime(endDate, endTime) > combineDateAndTime(startDate, startTime)
      ? undefined
      : { kind: 'invalidPeriod', message: 'End time must be after start time' };
  });
});

const initialPeriod = (): UsagePeriodModel => {
  const end = new Date();
  end.setSeconds(0, 0);
  const start = new Date(end.getTime() - 24 * 60 * 60 * 1000);

  return {
    startDate: new Date(start),
    startTime: new Date(start),
    endDate: new Date(end),
    endTime: new Date(end),
  };
};

@Component({
  selector: 'mce-shared-billing-usage-dialog',
  imports: [
    DatePipe, DecimalPipe, PercentPipe, FormField, MatButtonModule, MatDatepickerModule,
    MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatProgressBarModule, MatTableModule, MatTimepickerModule,
  ],
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }],
  templateUrl: './shared-billing-usage-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedBillingUsageDialogComponent {
  private readonly api = inject(MceApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly summary = signal<UserCreditSummaryDto[]>([]);

  protected readonly periodModel = signal<UsagePeriodModel>(initialPeriod());
  protected readonly periodForm = form(this.periodModel, usagePeriodSchema);
  protected readonly loadedPeriod = signal<{ start: Date; end: Date } | undefined>(undefined);
  protected readonly loadError = signal('');
  protected readonly columns = ['email', 'credits', 'percent'];
  protected readonly totalCredits = computed(() =>
    this.summary().reduce((total, row) => total + (row.totalCredits ?? 0), 0));
  protected readonly rows = computed(() => {
    const total = this.totalCredits();
    return this.summary().map(row => {
      const email = appOpt(row.email);
      const credits = row.totalCredits ?? 0;
      return {
        email: isSome(email) && email.value ? email.value : 'Email unavailable',
        credits,
        percent: total === 0 ? 0 : credits / total,
      };
    });
  });

  protected loadUsage(event: Event): void {
    event.preventDefault();
    if (this.periodForm().submitting()) return;
    submit(this.periodForm, async () => {
      const period = this.periodModel();
      const start = combineDateAndTime(period.startDate, period.startTime);
      const end = combineDateAndTime(period.endDate, period.endTime);
      this.loadError.set('');
      this.loadedPeriod.set(undefined);
      this.summary.set([]);
      try {
        const summary = await firstValueFrom(this.api.v1SharedBillingUsage(start, end).pipe(
          takeUntilDestroyed(this.destroyRef),
        ));
        if (this.destroyRef.destroyed) return;
        this.summary.set(summary);
        this.loadedPeriod.set({ start, end });
      } catch (error) {
        if (!this.destroyRef.destroyed) this.loadError.set(getApiErrorMessage(error));
      }
    });
  }
}
